// Recordings admin — manage who has access, provision new agents, and review
// the access-audit log. Admin-only: the page self-gates on recordingsRole ===
// 'admin', and every underlying operation is authorized server-side too (the
// callables check the caller's role; the audit read is gated by rules).

import { useCallback, useEffect, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import { getCurrentUser, readUser } from '../firebase';
import {
  listRecentAudit,
  provisionRecordingsAgents,
  setRecordingsRole,
} from '../recordings/firebase';
import { AccessAudit, RecordingsRole, resolveRecordingsRole } from '../recordings/types';
import { RECORDINGS_ROLE_LABELS, RECORDINGS_ROLE_ORDER } from '../recordings/constants';

type Access = { kind: 'loading' } | { kind: 'denied' } | { kind: 'ok' };

function fmtWhen(ms: number): string {
  try {
    return ms ? new Date(ms).toLocaleString() : '—';
  } catch {
    return '—';
  }
}

const RecordingsAdmin: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });

  const [roleEmail, setRoleEmail] = useState('');
  const [role, setRole] = useState<RecordingsRole>('agent');
  const [roleMsg, setRoleMsg] = useState('');
  const [roleBusy, setRoleBusy] = useState(false);

  const [bulkText, setBulkText] = useState('');
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const [audit, setAudit] = useState<AccessAudit[]>([]);
  const [auditBusy, setAuditBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const r = resolveRecordingsRole(data && data.restricted);
        if (!cancelled) setAccess({ kind: r === 'admin' ? 'ok' : 'denied' });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditBusy(true);
    try {
      setAudit(await listRecentAudit(100));
    } catch {
      /* rules will reject non-admins; page is already gated */
    } finally {
      setAuditBusy(false);
    }
  }, []);

  useEffect(() => {
    if (access.kind === 'ok') loadAudit();
  }, [access.kind, loadAudit]);

  async function submitRole(e: React.FormEvent) {
    e.preventDefault();
    setRoleBusy(true);
    setRoleMsg('');
    try {
      const res = await setRecordingsRole(roleEmail.trim(), role);
      setRoleMsg(`Set ${res.email} → ${res.role}.`);
      setRoleEmail('');
    } catch (err: any) {
      setRoleMsg(err?.message || 'Failed to set role.');
    } finally {
      setRoleBusy(false);
    }
  }

  async function revokeRole() {
    if (!roleEmail.trim()) return;
    setRoleBusy(true);
    setRoleMsg('');
    try {
      const res = await setRecordingsRole(roleEmail.trim(), null);
      setRoleMsg(`Revoked access for ${res.email}.`);
      setRoleEmail('');
    } catch (err: any) {
      setRoleMsg(err?.message || 'Failed to revoke.');
    } finally {
      setRoleBusy(false);
    }
  }

  async function submitBulk(e: React.FormEvent) {
    e.preventDefault();
    const emails = bulkText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    setBulkBusy(true);
    setBulkMsg('');
    try {
      const res = await provisionRecordingsAgents(emails, 'agent');
      const ok = res.results.filter((r) => r.ok).length;
      const failed = res.results.filter((r) => !r.ok);
      setBulkMsg(
        `Provisioned ${ok}/${res.count} as agent.` +
          (failed.length ? ` Failed: ${failed.map((f) => `${f.email} (${f.error})`).join(', ')}` : ''),
      );
      if (!failed.length) setBulkText('');
    } catch (err: any) {
      setBulkMsg(err?.message || 'Provisioning failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  if (access.kind === 'loading') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ padding: '40px 28px' }}>
              <IonSpinner />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }
  if (access.kind === 'denied') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ maxWidth: 640, padding: '40px 28px' }}>
              <div className="pm-header">
                <div className="pm-header-titles">
                  <h1>Recordings admin</h1>
                  <p>Admins only.</p>
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const card: React.CSSProperties = {
    background: 'var(--rr-paper, #fff)',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 'var(--pm-radius-lg, 12px)',
    padding: '16px 18px',
    marginTop: 16,
  };
  const input: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 8,
    fontSize: 14,
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 1040, padding: '24px 28px' }}>
            <div
              className="pm-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
            >
              <div className="pm-header-titles">
                <h1>Recordings admin</h1>
                <p>Grant access, provision agents, and review the access-audit log.</p>
              </div>
              <button
                type="button"
                className="pm-btn"
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                onClick={() => history.push('/Recordings')}
              >
                ← Recordings
              </button>
            </div>

            {/* Set / revoke role */}
            <form onSubmit={submitRole} style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Set a user’s access</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  style={{ ...input, flex: '1 1 260px' }}
                  placeholder="email@rolloverrep.com"
                  value={roleEmail}
                  onChange={(e) => setRoleEmail(e.target.value)}
                />
                <select style={input} value={role} onChange={(e) => setRole(e.target.value as RecordingsRole)}>
                  {RECORDINGS_ROLE_ORDER.map((r) => (
                    <option key={r} value={r}>
                      {RECORDINGS_ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <button type="submit" className="pm-btn pm-btn-primary" style={{ padding: '8px 16px' }} disabled={roleBusy}>
                  {roleBusy ? '…' : 'Set role'}
                </button>
                <button type="button" className="pm-btn" style={{ padding: '8px 16px' }} disabled={roleBusy} onClick={revokeRole}>
                  Revoke
                </button>
              </div>
              {roleMsg && <div style={{ marginTop: 8, fontSize: 13 }}>{roleMsg}</div>}
              <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                The account must already exist. To create new agents, use bulk provisioning below.
              </p>
            </form>

            {/* Bulk provision */}
            <form onSubmit={submitBulk} style={card}>
              <h2 style={{ margin: '0 0 10px', fontSize: 16 }}>Bulk-provision agents</h2>
              <textarea
                style={{ ...input, width: '100%', minHeight: 90, fontFamily: 'monospace' }}
                placeholder="Paste emails, separated by spaces, commas, or newlines"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                <button type="submit" className="pm-btn pm-btn-primary" style={{ padding: '8px 16px' }} disabled={bulkBusy}>
                  {bulkBusy ? 'Provisioning…' : 'Provision as agents'}
                </button>
                <span style={{ fontSize: 12, color: '#888' }}>
                  Creates accounts (no password) — agents set one via “reset password.”
                </span>
              </div>
              {bulkMsg && <div style={{ marginTop: 8, fontSize: 13 }}>{bulkMsg}</div>}
            </form>

            {/* Audit log */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>Access audit log</h2>
                <button className="pm-btn" style={{ padding: '6px 12px' }} onClick={loadAudit} disabled={auditBusy}>
                  {auditBusy ? '…' : 'Refresh'}
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <th style={thTd}>When</th>
                      <th style={thTd}>Who</th>
                      <th style={thTd}>Action</th>
                      <th style={thTd}>Call ID</th>
                      <th style={thTd}>Target agent</th>
                      <th style={thTd}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((a) => (
                      <tr key={a.id} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <td style={thTd}>{fmtWhen(a.created_at)}</td>
                        <td style={thTd}>{a.email}</td>
                        <td style={thTd}>{a.action}</td>
                        <td style={thTd}>{a.call_id || '—'}</td>
                        <td style={thTd}>{a.target_agent_email || '—'}</td>
                        <td style={thTd}>{a.ip || '—'}</td>
                      </tr>
                    ))}
                    {audit.length === 0 && !auditBusy && (
                      <tr>
                        <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          No audit entries yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

const thTd: React.CSSProperties = { padding: '9px 12px', whiteSpace: 'nowrap' };

export default RecordingsAdmin;
